import React, { useState, useEffect } from 'react';

import Representative from '~/components/ProjectSections/Representative';
import Client from '~/components/ProjectSections/Client';
import ClientContact from '~/components/ProjectSections/ClientContact';
import ClientPatterns from '~/components/ProjectSections/ClientPatterns';
import CompletionFiles from '~/components/ProjectSections/CompletionFiles';
import Files from '~/components/ProjectSections/Files';
import Invoices from '~/components/ProjectSections/Invoices';
import Main from '~/components/ProjectSections/Main';
import RebateMovement from '~/components/ProjectSections/RebateMovement';
import Records from '~/components/ProjectSections/Records';
import RequestFiles from '~/components/ProjectSections/RequestFiles';

import api from '~/services/api';

interface IRebateProps {
  project_id: string;
  all?: boolean;
}

interface IProject {
  id: string;
  type: string;
  code: string;
  erp_client_code: string;
  erp_rep_code: string;
  note: string;
  client_contact_name: string;
  client_contact_phone: string;
  client_contact_email: string;
  project_procedure: {
    id: string;
    sequence: number;
  };
}

const Rebate: React.FC<IRebateProps> = ({ project_id, all }) => {
  const [project, setProject] = useState<IProject>();

  useEffect(() => {
    api.get(`/projects/id?id=${project_id}`).then(response => {
      setProject(response.data);
    });
  }, [project_id]);

  const handleRepresentative = (): React.ReactNode | null => {
    if (project) {
      return (
        <Representative
          erp_rep_code={project.erp_rep_code ? project.erp_rep_code : ''}
        />
      );
    }

    return null;
  };

  const handleClient = (): React.ReactNode | null => {
    if (project) {
      return (
        <Client
          rep_as_client={false}
          erp_client_code={
            project.erp_client_code ? project.erp_client_code : ''
          }
          erp_rep_code=""
        />
      );
    }

    return null;
  };

  const handleClientPatterns = (): React.ReactNode | null => {
    if (project) {
      return (
        <ClientPatterns
          erp_client_code={
            project.erp_client_code ? project.erp_client_code : ''
          }
        />
      );
    }

    return null;
  };

  const handleMain = (): React.ReactNode | null => {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <Main
            code={project.code ? project.code : ''}
            note={project.note ? project.note : ''}
          />
        );
      }
    }

    return null;
  };

  const handleRebateMovement = (): React.ReactNode | null => {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return <RebateMovement project_id={project.id} />;
      }
    }

    return null;
  };

  const handleClientContact = (): React.ReactNode | null => {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <ClientContact
            client_contact_name={
              project.client_contact_name ? project.client_contact_name : ''
            }
            client_contact_phone={
              project.client_contact_phone ? project.client_contact_phone : ''
            }
            client_contact_email={
              project.client_contact_email ? project.client_contact_email : ''
            }
          />
        );
      }
    }

    return null;
  };

  const handleRecords = (): React.ReactNode | null => {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return <Records project_id={project.id} />;
      }
    }

    return null;
  };

  const handleFiles = (): React.ReactNode | null => {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <Files
            project_id={project.id}
            project_procedure_id={project.project_procedure.id}
          />
        );
      }
    }

    return null;
  };

  const handleRequestFiles = (): React.ReactNode | null => {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <RequestFiles
            project_id={project.id}
            project_procedure_id={project.project_procedure.id}
          />
        );
      }
    }

    return null;
  };

  const handleCompletionFiles = (): React.ReactNode | null => {
    if (project) {
      if (project.project_procedure.sequence > 3 || all) {
        return (
          <CompletionFiles
            project_id={project.id}
            project_procedure_id={project.project_procedure.id}
          />
        );
      }
    }

    return null;
  };

  const handleInvoices = (): React.ReactNode | null => {
    if (project) {
      if (project.project_procedure.sequence === 5 || all) {
        return <Invoices project_id={project.id} />;
      }
    }

    return null;
  };

  return (
    <>
      {handleRepresentative()}
      {handleClient()}
      {handleClientPatterns()}
      {handleMain()}
      {handleRebateMovement()}
      {handleClientContact()}
      {handleRecords()}
      {handleFiles()}
      {handleRequestFiles()}
      {handleCompletionFiles()}
      {handleInvoices()}
    </>
  );
};

export default Rebate;
